import React, { useState, useEffect } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { FaEnvelope, FaPhone, FaGlobe, FaMoneyBillWave, FaProjectDiagram, FaClock } from 'react-icons/fa';
import './styles/customerCard.css';

function CustomerCard({ customer, statusColor, onClick }) {
  const [accountOwner, setAccountOwner] = useState(null);
  const [customerData, setCustomerData] = useState(customer);

  // Lyt efter ændringer i kundens data
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'customers', customer.id), (doc) => {
      if (doc.exists()) {
        setCustomerData({ id: doc.id, ...doc.data() });
      }
    });

    return () => unsubscribe();
  }, [customer.id]);

  useEffect(() => {
    if (customerData.accountOwner) {
      fetchAccountOwner();
    }
  }, [customerData.accountOwner]);

  const fetchAccountOwner = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', customerData.accountOwner));
      if (userDoc.exists()) {
        setAccountOwner(userDoc.data());
      }
    } catch (error) {
      console.error('Fejl ved hentning af account owner:', error);
    }
  };

  // Formatter til DKK
  const formatCurrency = (amount) => {
    if (!amount) return '';
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getDaysSinceLastUpdate = () => {
    if (!customerData.updates || customerData.updates.length === 0) return null;
    
    const lastUpdate = customerData.updates.reduce((latest, current) => {
      return new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest;
    });
    
    const days = Math.floor((new Date() - new Date(lastUpdate.timestamp)) / (1000 * 60 * 60 * 24));
    
    // Hvis det er et vundet lead, returner normal status uanset antal dage
    if (customerData.status === 'won') {
      return {
        days,
        text: days === 0 ? 'I dag' : days === 1 ? 'I går' : `${days} dage siden`,
        status: 'normal'
      };
    }

    // Hvis der er sat en næste kontakt dato
    if (customerData.nextContact) {
      const nextContactDate = new Date(customerData.nextContact);
      const now = new Date();
      
      // Hvis næste kontakt dato ikke er overskredet, vis normal status
      if (nextContactDate > now) {
        const daysUntil = Math.ceil((nextContactDate - now) / (1000 * 60 * 60 * 24));
        return {
          days,
          text: `Kontakt om ${daysUntil} ${daysUntil === 1 ? 'dag' : 'dage'}`,
          status: 'normal'
        };
      }
      
      // Hvis næste kontakt dato er overskredet
      const daysOverdue = Math.floor((now - nextContactDate) / (1000 * 60 * 60 * 24));
      return {
        days,
        text: `${daysOverdue} ${daysOverdue === 1 ? 'dag' : 'dage'} overskredet`,
        status: daysOverdue > 7 ? 'critical' : 'warning'
      };
    }
    
    // Hvis ingen næste kontakt er sat, brug standard regler
    return {
      days,
      text: days === 0 ? 'I dag' : days === 1 ? 'I går' : `${days} dage siden`,
      status: days > 14 ? 'critical' : days > 7 ? 'warning' : 'normal'
    };
  };

  const daysSinceLastContact = getDaysSinceLastUpdate();

  return (
    <div className="customer-card" onClick={onClick}>
      <div className="status-indicator" style={{ background: statusColor }} />
      
      <div className="card-header">
        <div className="header-top">
          <h3>{customerData.name}</h3>
          <div className="customer-indicators">
            {customerData.projectId && (
              <div className="project-indicator" title="Har tilknyttet projekt">
                <FaProjectDiagram />
              </div>
            )}
            {daysSinceLastContact && (
              <div 
                className={`last-contact-indicator ${daysSinceLastContact.status}`}
                title={daysSinceLastContact.status === 'critical' 
                  ? 'Kritisk: Over 14 dage siden sidste kontakt'
                  : daysSinceLastContact.status === 'warning'
                  ? 'Advarsel: Over 7 dage siden sidste kontakt'
                  : 'Sidste kontakt'}
              >
                <FaClock />
                <span>{daysSinceLastContact.text}</span>
              </div>
            )}
          </div>
        </div>
        {accountOwner && (
          <div className="account-owner" title={`Ejer: ${accountOwner.name || accountOwner.email}`}>
            {accountOwner.photoURL ? (
              <img 
                src={accountOwner.photoURL} 
                alt="" 
                className="owner-photo"
              />
            ) : (
              <div className="owner-photo-placeholder">
                {accountOwner.name ? accountOwner.name.charAt(0).toUpperCase() : '?'}
              </div>
            )}
            <span className="owner-name">{accountOwner.name || accountOwner.email}</span>
          </div>
        )}
      </div>

      <div className="card-content">
        {customerData.email && (
          <div className="info-row">
            <FaEnvelope className="info-icon" />
            <span>{customerData.email}</span>
          </div>
        )}
        
        {customerData.phone && (
          <div className="info-row">
            <FaPhone className="info-icon" />
            <span>{customerData.phone}</span>
          </div>
        )}

        {customerData.websiteUrl && (
          <div className="info-row">
            <FaGlobe className="info-icon" />
            <span className="truncate">{customerData.websiteUrl}</span>
          </div>
        )}

        {customerData.estimatedValue && (
          <div className="info-row highlight">
            <FaMoneyBillWave className="info-icon" />
            <span>{formatCurrency(customerData.estimatedValue)}</span>
          </div>
        )}
      </div>

      <div className="card-footer">
        {customerData.products && customerData.products.length > 0 && (
          <div className="tags-container">
            {customerData.products.map(product => (
              <span key={product} className="tag product-tag">
                {PRODUCTS.find(p => p.id === product)?.label || product}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Tilføj PRODUCTS konstant her så vi kan vise labels i stedet for IDs
const PRODUCTS = [
  { id: 'website', label: 'Hjemmeside' },
  { id: 'video', label: 'Video' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'automation', label: 'Automatisering' }
];

export default CustomerCard;
